import { apiMutate } from "~/utils/apiClient";

// Quick action definitions are admin config for the platform's quick-action
// cards (agent/dataset/home surfaces), served by the Projects service under
// the same `/api/projects/v1` prefix as projects themselves.
const { VITE_PROJECTS_API } = import.meta.env;
export const QUICK_ACTIONS_API_PREFIX = `${VITE_PROJECTS_API}/quick-actions`;

export const quickActionListPath = (params?: { limit?: number; offset?: number }) => {
  const query = new URLSearchParams();
  if (params?.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  if (params?.offset !== undefined) {
    query.set("offset", String(params.offset));
  }
  const qs = query.toString();
  return qs ? `${QUICK_ACTIONS_API_PREFIX}?${qs}` : QUICK_ACTIONS_API_PREFIX;
};

export const quickActionPath = (id: string) => `${QUICK_ACTIONS_API_PREFIX}/${id}`;

export const QuickActionPlacement = {
  AGENT: "agent",
  DATASET: "dataset",
  HOME: "home",
} as const;
export type QuickActionPlacement = (typeof QuickActionPlacement)[keyof typeof QuickActionPlacement];

export interface QuickActionAgentRef {
  agent_id: string;
}

export interface QuickActionDatasetRef {
  dataset_id: string;
}

// The full admin definition, as returned by create/list/get/update.
export interface QuickAction {
  id: string;
  placement: QuickActionPlacement;
  placement_id: string | null;
  title: string;
  subtitle: string | null;
  icon: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  project_name: string;
  project_description: string | null;
  seed_prompt: string | null;
  agents: QuickActionAgentRef[];
  datasets: QuickActionDatasetRef[];
}

export interface QuickActionListMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface QuickActionList {
  items: QuickAction[];
  metadata: QuickActionListMetadata;
}

export interface CreateQuickActionInput {
  placement: QuickActionPlacement;
  placement_id?: string | null;
  title: string;
  subtitle?: string | null;
  icon?: string | null;
  enabled?: boolean;
  project_name: string;
  project_description?: string | null;
  seed_prompt?: string | null;
  agent_ids?: string[];
  dataset_ids?: string[];
}

// Placement is immutable after creation — recreate the definition to move it
// to a different surface. Every field is optional; `agent_ids`/`dataset_ids`
// set-replace the attached collections when supplied.
export interface UpdateQuickActionInput {
  title?: string;
  subtitle?: string | null;
  icon?: string | null;
  enabled?: boolean;
  project_name?: string;
  project_description?: string | null;
  seed_prompt?: string | null;
  agent_ids?: string[];
  dataset_ids?: string[];
}

export async function createQuickAction(input: CreateQuickActionInput): Promise<QuickAction> {
  return apiMutate<QuickAction>(QUICK_ACTIONS_API_PREFIX, "POST", input);
}

export async function updateQuickAction(
  id: string,
  input: UpdateQuickActionInput
): Promise<QuickAction> {
  return apiMutate<QuickAction>(quickActionPath(id), "PATCH", input);
}

export async function deleteQuickAction(id: string): Promise<void> {
  await apiMutate<null>(quickActionPath(id), "DELETE", undefined, { expects204: true });
}
